import React, { useState, FormEvent, ChangeEvent } from 'react';
import { ContactService } from '../service/contactService';
import { MailIcon, AlertTriangleIcon, CheckCircleIcon, CodeIcon, LoaderIcon, WhatsappIcon } from '../icons/icons'; // adjust import paths

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type MessageState = {
  type: 'success' | 'error' | '';
  text: string;
};

const CONTACT_US_SERVICE = 'https://us-central1-stedwaytech.cloudfunctions.net/st_email_service/api/email';

export const ContactSection = () => {
  const WHATSAPP_NUMBER = '+27794670171';
  const PREFILLED_MESSAGE = 'Hello StedwayTech, I saw your website and I\'m interested in discussing a new project.';
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILLED_MESSAGE)}`;

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [messageState, setMessageState] = useState<MessageState>({ type: '', text: '' });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = (): boolean => {
    const { name, email, subject, message } = formData;
    if (!name || !email || !subject || !message) {
      setMessageState({ type: 'error', text: 'Please fill in all fields.' });
      return false;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(email)) {
      setMessageState({ type: 'error', text: 'Please enter a valid email address.' });
      return false;
    }

    return true;
  };

  const sendEnquiry = async (e: FormEvent) => {
    e.preventDefault();
    setMessageState({ type: '', text: '' });

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await ContactService.sendEnquiry(CONTACT_US_SERVICE, formData);

      setMessageState({ type: 'success', text: response.message });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setMessageState({ type: 'error', text: `Error: ${errorMessage}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">

          {/* Info Box */}
          <div className="mb-8 lg:mb-0 p-8 md:p-10 rounded-3xl bg-teal-700 shadow-2xl text-white relative overflow-hidden flex flex-col justify-between">
            <div className="absolute inset-0 opacity-10 bg-repeat" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg viewBox=\"0 0 10 10\" xmlns=\"http://www.w3.org/2000/svg\"><circle cx=\"5\" cy=\"5\" r=\"3\" fill=\"%23ffffff\"/></svg>')", backgroundSize: '20px 20px' }}></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-orange-300">Ready to Build?</h2>
              <p className="text-teal-100 mb-8 text-base md:text-lg">
                Tell us your scope, timeline, and goals. We translate great ideas into performant, scalable reality.
              </p>

              <div className="space-y-4 pt-4 border-t border-teal-500">
                <div className="flex items-start">
                  <a
                    href="mailto:nthabi.freelance@gmail.com?subject=New%20Project%20Enquiry&body=Hi%20StedwayTech%2C%0A%0AI%20have%20a%20project%20I’d%20like%20to%20discuss."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start transition duration-150 hover:bg-teal-600/20 p-2 -m-2 rounded-lg"
                    >
                    <MailIcon className="w-6 h-6 mr-3 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <span className="font-semibold block">Email Us Directly</span>
                        <small>Click here to send a mail</small>
                    </div>
                </a>

                </div>

                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start transition duration-150 hover:bg-teal-600/20 p-2 -m-2 rounded-lg"
                >
                  <WhatsappIcon className="w-6 h-6 mr-3 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold block">Chat via WhatsApp</span>
                    <small>Click icon to start chatting</small>
                  </div>
                </a>

                <div className="flex items-start">
                  <CodeIcon className="w-6 h-6 mr-3 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold block">Modern Code Solutions</span>
                    <span className="text-teal-200 text-sm">Tailored to you, made by us.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="p-6 md:p-8 bg-white rounded-3xl shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Or, Send a Detailed Message</h3>

            {messageState.text && (
              <div className={`p-4 mb-4 rounded-xl flex items-center transition-opacity duration-300 ${messageState.type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'}`}>
                {messageState.type === 'success' ? (
                  <CheckCircleIcon className="w-5 h-5 mr-3 text-green-500" />
                ) : (
                  <AlertTriangleIcon className="w-5 h-5 mr-3 text-red-500" />
                )}
                {messageState.text}
              </div>
            )}

            <form onSubmit={sendEnquiry} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} disabled={loading} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 transition duration-150" />
                <input type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} disabled={loading} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 transition duration-150" />
              </div>
              <input type="text" name="subject" placeholder="Project Subject (e.g., New E-commerce Site)" value={formData.subject} onChange={handleChange} disabled={loading} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 transition duration-150" />
              <textarea name="message" rows={5} placeholder="Tell us about your project..." value={formData.message} onChange={handleChange} disabled={loading} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 transition duration-150 resize-none" />
              <button type="submit" disabled={loading} className={`w-full py-3 px-6 text-lg font-semibold text-white rounded-xl shadow-lg transition duration-300 flex items-center justify-center space-x-3 ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 transform hover:scale-[1.01]'}`}>
                {loading ? (
                  <>
                    <LoaderIcon className="w-5 h-5 text-white animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <MailIcon className="w-5 h-5 text-white" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactSection;
